import { NinjaArt } from "../art/NinjaArt";
import { formatUnlockDate } from "../components/DetailCard";
import { SlotRow } from "../components/SlotRow";
import { NINJAS, STARTER_NINJA, type Ninja } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { fighterFor, isKnown, isRecruited, recordAgainst } from "../game/roster";
import { lookFor, setModes } from "../game/sets";
import type { SaveState } from "../save/schema";
import "./Ladder.css";
import "./Roster.css";

interface Props {
  save: SaveState;
  onOpenCard: (rankIndex: number) => void;
  onBack: () => void;
}

/** The status line under a ninja's name in the binder. */
export function rosterLine(save: SaveState, n: Ninja): string {
  const rec = save.roster[n.id];
  if (n.id === STARTER_NINJA) return "With you from the start";
  if (rec?.recruited) return `Recruited ${formatUnlockDate(rec.recruited)}`;
  const { fought } = recordAgainst(save.roster, n.id);
  return `${n.health} health` + (fought ? ` · fought ${fought}` : "");
}

/** The binder: a slot for every ninja, silhouettes for the ones not yet recruited. */
export function Roster({ save, onOpenCard, onBack }: Props) {
  const fighter = fighterFor(save.roster, save.settings.fighter);
  const modes = setModes(save.settings.ops);
  const total = NINJAS.filter((n) => isRecruited(save.roster, n.id)).length;
  return (
    <section className="screen ladder-screen roster-screen">
      <h2 className="display ladder-title">My ninjas</h2>
      <p className="roster-sub">
        {total} of {NINJAS.length} recruited. Fighting with <b>{fighter.name}</b>. Tap a ninja to
        fit items or change fighter.
      </p>
      <ul className="ladder roster">
        {NINJAS.map((n) => {
          const r = RANKS[n.rankIndex]!;
          const recruited = isRecruited(save.roster, n.id);
          const known = isKnown(save.rank, save.roster, n);
          const look = lookFor(save.rank, save.settings.look, n.rankIndex, modes);
          const name = known ? n.name : "???";
          const isFighter = n.id === fighter.id;
          return (
            <li key={n.id}>
              <button
                type="button"
                className={[
                  "ladder-row",
                  "roster-row",
                  recruited ? "" : "locked",
                  isFighter ? "here" : "",
                ]
                  .join(" ")
                  .replace(/\s+/g, " ")
                  .trim()}
                style={{ "--c": r.colour } as React.CSSProperties}
                aria-label={
                  `${known ? n.name : "Mystery ninja"}, ${rosterLine(save, n)}` +
                  (recruited ? ", recruited" : ", not recruited") +
                  (isFighter ? ", your fighter" : "")
                }
                onClick={() => onOpenCard(n.rankIndex)}
              >
                <NinjaArt
                  rank={r}
                  variant={recruited ? look : undefined}
                  locked={!recruited}
                  className="ladder-art"
                />
                <span className="ladder-name display">{name}</span>
                <span className="ladder-score">{rosterLine(save, n)}</span>
                <SlotRow
                  ninja={n}
                  loadouts={recruited ? save.settings.loadout : undefined}
                  items={save.items}
                  locked={!recruited}
                  className="ladder-slots"
                />
                {isFighter && <span className="roster-badge display">Fighting</span>}
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="big display ladder-back" onClick={onBack}>
        Back
      </button>
    </section>
  );
}
